import { useEffect, useState } from "react";
import { Plus, Trash2, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { api, brl, fmtDate, formatApiError, PROJECT_STATUSES, UNITS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyProject = { name: "", client: "", unit: "iatech", status: "planejamento", deadline: "", value: "", notes: "" };

const statusStyle = {
  planejamento: "text-zinc-300 border-zinc-600",
  andamento: "text-nx-yellow border-nx-yellow/50",
  revisao: "text-amber-300 border-amber-500/40",
  entregue: "text-green-400 border-green-500/40",
};

const inputCls =
  "w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-3.5 py-2.5 text-sm text-white transition-colors";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function Projetos() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [filterUnit, setFilterUnit] = useState("");
  const [form, setForm] = useState(emptyProject);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const params = filterUnit ? { unit: filterUnit } : {};
    api.get("/projects", { params }).then((r) => setProjects(r.data)).catch(() => {});
  }, [filterUnit]);

  const addProject = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", { ...form, value: parseFloat(form.value) || 0 });
      setProjects((ps) => [data, ...ps]);
      setForm(emptyProject);
      setShowForm(false);
      toast.success("Projeto criado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/projects/${id}`, { status });
      setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, status } : p)));
      toast.success("Status atualizado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((ps) => ps.filter((p) => p.id !== id));
      toast.success("Projeto removido");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">Operação</p>
          <h1 className="font-syne text-3xl sm:text-4xl font-extrabold mt-2" data-testid="projects-title">Projetos</h1>
        </div>
        <div className="flex gap-3 items-center">
          <select value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)} className={`${inputCls} !w-auto`} data-testid="projects-filter-unit">
            <option value="">Todas as unidades</option>
            {Object.entries(UNITS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            onClick={() => setShowForm(true)}
            data-testid="projects-add-button"
            className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark text-sm font-bold px-5 py-2.5 rounded-full hover:bg-nx-electric transition-colors"
          >
            <Plus className="w-4 h-4" /> Novo Projeto
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-grid">
        {projects.map((p) => (
          <div key={p.id} className="nx-card card-hover p-5" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold leading-snug">{p.name}</p>
                <p className="text-[11px] text-zinc-500 mt-1">{p.client || "Sem cliente"} · {UNITS[p.unit]}</p>
              </div>
              {user?.role === "admin" && (
                <button onClick={() => removeProject(p.id)} data-testid={`project-delete-${p.id}`} className="text-zinc-600 hover:text-red-400 transition-colors shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 text-zinc-400">
                <CalendarDays className="w-3.5 h-3.5 text-zinc-500" />
                {fmtDate(p.deadline)}
              </span>
              <span className="font-mono font-bold text-nx-yellow">{brl(p.value)}</span>
            </div>
            {p.notes && <p className="mt-3 text-xs text-zinc-500 leading-relaxed line-clamp-2">{p.notes}</p>}
            <select
              value={p.status}
              onChange={(e) => setStatus(p.id, e.target.value)}
              data-testid={`project-status-${p.id}`}
              className={`mt-4 w-full bg-zinc-800/60 border rounded-lg px-2.5 py-2 text-[11px] font-mono uppercase focus:outline-none focus:border-nx-yellow ${statusStyle[p.status]}`}
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        ))}
        {projects.length === 0 && (
          <div className="nx-card p-10 text-center text-zinc-500 md:col-span-2 xl:col-span-3" data-testid="projects-empty">
            Nenhum projeto encontrado.
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="project-form-modal">
          <form onSubmit={addProject} className="glass rounded-3xl p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne text-xl font-bold">Novo Projeto</h2>
              <button type="button" onClick={() => setShowForm(false)} data-testid="project-form-close" className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Nome *">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="project-name-input" />
                </Field>
              </div>
              <Field label="Cliente">
                <input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className={inputCls} data-testid="project-client-input" />
              </Field>
              <Field label="Unidade">
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls} data-testid="project-unit-select">
                  {Object.entries(UNITS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="Prazo">
                <DatePicker value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} testid="project-deadline-input" />
              </Field>
              <Field label="Valor (R$)">
                <input type="number" min="0" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} data-testid="project-value-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Anotações">
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="project-notes-input" />
                </Field>
              </div>
            </div>
            <button type="submit" data-testid="project-submit-button" className="mt-6 w-full bg-nx-yellow text-nx-dark font-bold py-3 rounded-xl hover:bg-nx-electric transition-colors">
              Salvar projeto
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
